import React, { useCallback, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { formatISO, parseISO, subDays } from "date-fns";
import { ConnectionProvider } from "@common/types/integrationConnection";
import { AccountingMigrationExternalSystem } from "@common/types/domains/accounting/accountingMigration";
import { PlainModal } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useCreateAccountingMigrationMutation } from "@api/accountingIntegration.api";
import { useIntegrationConnections } from "@api/integrationConnection.api";
import { getApiErrorMessage, getApiErrorStatus } from "@api/apiError";
import { useEntitiesById } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useOverlays } from "contexts/OverlayContext";
import { useScope } from "state/scopeContext.lib";
import { trackEvent } from "@lib/analytics";
import { AnalyticsEvent } from "@common/constants/analytics-events";
import {
    ACCOUNTING_MIGRATION_CONNECTION_NOT_FOUND_MESSAGE,
    ACCOUNTING_MIGRATION_QUICKBOOKS_RECONNECT_REQUIRED_MESSAGE,
} from "@common/constants/accounting-migration";
import { SUPPORTED_MIGRATION_SYSTEMS } from "@components/accountingMigration/source";
import { MigrationFormValues } from "./accountingMigrationForm";
import { SelectMigrationEntity } from "./SelectMigrationEntity";
import { MigrationSourceStep } from "./MigrationSourceStep";
import { MigrationHistoricalDataStep } from "./MigrationHistoricalDataStep";
import { MigrationComingSoonStep } from "./MigrationComingSoonStep";
import { useQuickbooksMigrationSource } from "./useQuickbooksMigrationSource";
import { getQuickbooksConnectionLabel } from "./quickbooksConnectionLabel";

enum MigrationStep {
    SelectEntity = "selectEntity",
    Source = "source",
    ComingSoon = "comingSoon",
    HistoricalData = "historicalData",
}

const isSupportedSystem = (
    system: AccountingMigrationExternalSystem | null,
    flags: { qboFileMigration: boolean },
): boolean => {
    if (system === AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE_GL_COA) {
        return flags.qboFileMigration;
    }
    return SUPPORTED_MIGRATION_SYSTEMS.includes(system);
};

const deriveMigrationEndDate = (
    bookkeepingStartDate: string | undefined,
): string | undefined => {
    if (!bookkeepingStartDate) {
        return undefined;
    }
    return formatISO(subDays(parseISO(bookkeepingStartDate), 1), {
        representation: "date",
    });
};

const getInitialHistoricalData = (endDate: string | undefined) => ({
    migrate: true,
    startDate: undefined,
    endDate,
    syncGeneralLedger: true,
    syncCounterparties: true,
    syncAttachments: true,
    syncClasses: true,
    syncCatalogItems: true,
    syncInvoices: true,
    syncBills: true,
});

interface AccountingMigrationModalProps {
    onClose: () => void;
}

const AccountingMigrationModal: React.FC<AccountingMigrationModalProps> = ({
    onClose,
}) => {
    const { workspaceId } = useScope();
    const { error: toastError } = useToaster();
    const entitiesById = useEntitiesById();
    const { qboFileMigration } = useTypedFlags();

    const {
        data: quickbooksConnections,
        isPending: isLoadingQuickbooksConnections,
        isFetching: isFetchingQuickbooksConnections,
        isError: hasQuickbooksConnectionsError,
        refetch: refetchQuickbooksConnections,
    } = useIntegrationConnections({ provider: ConnectionProvider.QUICKBOOKS });

    const [step, setStep] = useState(MigrationStep.SelectEntity);
    const [entityId, setEntityId] = useState<number | undefined>();

    const createMigrationMutation = useCreateAccountingMigrationMutation(
        workspaceId ?? "",
        entityId ?? 0,
    );

    const formik = useFormik<MigrationFormValues>({
        initialValues: {
            migrationMethod: null,
            historicalData: getInitialHistoricalData(undefined),
        },
        onSubmit: (values) => {
            if (!entityId || createMigrationMutation.isPending) {
                return;
            }

            const isQuickbooksMigration =
                values.migrationMethod ===
                AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE;
            if (
                isQuickbooksMigration &&
                (!selectedConnection ||
                    selectedConnection.entityId !== entityId ||
                    selectedConnection.error)
            ) {
                toastError(
                    selectedConnection?.error
                        ? ACCOUNTING_MIGRATION_QUICKBOOKS_RECONNECT_REQUIRED_MESSAGE
                        : "Select a QuickBooks company before continuing.",
                );
                setStep(MigrationStep.Source);
                return;
            }

            createMigrationMutation.mutate(
                {
                    startDate: values.historicalData.startDate,
                    integrationConnectionId: isQuickbooksMigration
                        ? selectedConnection?.id
                        : undefined,
                    externalSystem: values.migrationMethod,
                    syncChartOfAccounts: true,
                    syncGeneralLedger: values.historicalData.syncGeneralLedger,
                    syncCounterparties:
                        values.historicalData.syncCounterparties,
                    syncAttachments: values.historicalData.syncAttachments,
                    syncClasses: values.historicalData.syncClasses,
                    syncCatalogItems: values.historicalData.syncCatalogItems,
                    syncInvoices: values.historicalData.syncInvoices,
                    syncBills: values.historicalData.syncBills,
                },
                {
                    onSuccess: () => onClose(),
                    onError: (error) => {
                        const status = getApiErrorStatus(error);
                        const message = getApiErrorMessage(error, "");
                        const isBrokenConnectionError =
                            status === 400 &&
                            message ===
                                ACCOUNTING_MIGRATION_QUICKBOOKS_RECONNECT_REQUIRED_MESSAGE;
                        const isMissingConnectionError =
                            status === 404 &&
                            message ===
                                ACCOUNTING_MIGRATION_CONNECTION_NOT_FOUND_MESSAGE;
                        const shouldRecoverQuickbooksConnection =
                            isQuickbooksMigration &&
                            (isBrokenConnectionError ||
                                isMissingConnectionError);
                        if (shouldRecoverQuickbooksConnection) {
                            void refetchQuickbooksConnections();
                            setStep(MigrationStep.Source);
                            return;
                        }
                        toastError(
                            "Failed to create migration. Please try again.",
                        );
                    },
                },
            );
        },
    });

    const { setFieldValue, setValues } = formik;

    const onQuickbooksConnectionSelected = useCallback(() => {
        setFieldValue(
            "migrationMethod",
            AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE,
        );
    }, [setFieldValue]);

    const {
        selectedConnection,
        effectiveConnection,
        availableConnections,
        isPickerVisible,
        isPending,
        resetForEntity,
        selectSource,
        selectConnection,
        connectCompany,
        reconnectSelectedCompany,
    } = useQuickbooksMigrationSource({
        entityId,
        connections: quickbooksConnections,
        onSelect: onQuickbooksConnectionSelected,
    });

    const onEntitySubmit = useCallback(
        (selectedEntityId: number) => {
            setEntityId(selectedEntityId);
            const selectedEntity = entitiesById.get(selectedEntityId);
            createMigrationMutation.reset();
            setValues((currentValues) => ({
                migrationMethod: null,
                historicalData:
                    selectedEntityId === entityId
                        ? currentValues.historicalData
                        : getInitialHistoricalData(
                              deriveMigrationEndDate(
                                  selectedEntity?.bookkeepingStartDate,
                              ),
                          ),
            }));
            resetForEntity();
            setStep(MigrationStep.Source);
        },
        [
            createMigrationMutation,
            entitiesById,
            entityId,
            resetForEntity,
            setValues,
        ],
    );

    const onSourceSelect = useCallback(
        (system: AccountingMigrationExternalSystem | null) => {
            if (isPending) {
                return;
            }

            createMigrationMutation.reset();
            selectSource(system);
            setFieldValue("migrationMethod", system);

            if (
                system === AccountingMigrationExternalSystem.QUICKBOOKS_ONLINE
            ) {
                return;
            }

            if (!isSupportedSystem(system, { qboFileMigration })) {
                void trackEvent(
                    AnalyticsEvent.ACCOUNTING_MIGRATION_UNSUPPORTED_SOFTWARE_SELECTED,
                    { externalSystem: system ?? "other", entityId },
                );
                setStep(MigrationStep.ComingSoon);
                return;
            }

            setStep(MigrationStep.HistoricalData);
        },
        [
            createMigrationMutation,
            setFieldValue,
            qboFileMigration,
            entityId,
            isPending,
            selectSource,
        ],
    );

    const onUseAnotherSource = useCallback(() => {
        if (isPending) {
            return;
        }
        selectSource(null);
        setFieldValue("migrationMethod", null);
    }, [isPending, selectSource, setFieldValue]);

    const onContinueQuickbooks = useCallback(() => {
        const connection = effectiveConnection;
        const canContinueQuickbooks =
            !isPending &&
            connection &&
            connection.entityId === entityId &&
            !connection.error;
        if (canContinueQuickbooks) {
            selectConnection(connection);
            setStep(MigrationStep.HistoricalData);
        }
    }, [effectiveConnection, entityId, isPending, selectConnection]);

    const selectedEntityName = entityId
        ? entitiesById.get(entityId)?.name
        : undefined;

    let content: React.ReactNode;
    if (step === MigrationStep.SelectEntity || !entityId) {
        content = (
            <SelectMigrationEntity
                initialEntityId={entityId}
                onSubmit={onEntitySubmit}
            />
        );
    } else if (step === MigrationStep.Source) {
        content = (
            <MigrationSourceStep
                entityName={selectedEntityName}
                onSelect={onSourceSelect}
                onBack={() => {
                    if (!isPending) {
                        setStep(MigrationStep.SelectEntity);
                    }
                }}
                isConnecting={isPending}
                isLoadingQuickbooksConnections={
                    quickbooksConnections === undefined &&
                    (isLoadingQuickbooksConnections ||
                        isFetchingQuickbooksConnections)
                }
                hasQuickbooksConnectionsError={
                    quickbooksConnections === undefined &&
                    hasQuickbooksConnectionsError
                }
                showQuickbooksPicker={isPickerVisible}
                quickbooksConnections={availableConnections}
                selectedQuickbooksConnection={effectiveConnection}
                onSelectQuickbooksConnection={selectConnection}
                onUseAnotherSource={onUseAnotherSource}
                onContinueQuickbooks={onContinueQuickbooks}
                onConnectQuickbooks={connectCompany}
                onReconnectQuickbooks={reconnectSelectedCompany}
                onRetryQuickbooksConnections={() => {
                    void refetchQuickbooksConnections();
                }}
            />
        );
    } else if (step === MigrationStep.ComingSoon) {
        content = (
            <MigrationComingSoonStep
                entityName={selectedEntityName}
                onBack={() => setStep(MigrationStep.Source)}
            />
        );
    } else {
        content = (
            <MigrationHistoricalDataStep
                entityName={selectedEntityName}
                isQboConnected={!!selectedConnection}
                quickbooksCompanyName={
                    selectedConnection
                        ? getQuickbooksConnectionLabel(selectedConnection)
                        : undefined
                }
                onBack={() => {
                    if (!isPending) {
                        setStep(MigrationStep.Source);
                    }
                }}
                onSubmit={formik.submitForm}
                isSubmitting={createMigrationMutation.isPending}
            />
        );
    }

    return <FormikProvider value={formik}>{content}</FormikProvider>;
};

export const AccountingMigrationModalContainer: React.FC = () => {
    const { getOverlayInfo, closeOverlay, overlayIsClosing } = useOverlays();
    const overlayInfo = getOverlayInfo("accountingMigration");

    if (!overlayInfo) {
        return null;
    }

    return (
        <PlainModal show={!overlayIsClosing} onHide={closeOverlay} size="md">
            <AccountingMigrationModal onClose={closeOverlay} />
        </PlainModal>
    );
};
